/** Home (S20), the Get live checklist (S11) and What we did (S21). docs/50 §4.2, docs/52 §3.3. */
import { Banner, Button, Card, EmptyState, Icon, List, ProgressRing, Row, Stat, type IconName } from '@ff/app-ui';
import type { home as H } from '@ff/contracts';
import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import type { z } from 'zod';
import { errorText } from '../lib/api.ts';
import { invalidate, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { ago, dayLabel, monthLabel, partOfDay, shortDate } from '../lib/format.ts';
import { location, scoped } from '../lib/router.ts';
import { can, firstName, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { toast, tz } from '../lib/ui.ts';
import { InstallCard, PushCard } from '../pwa/cards.tsx';
import { S } from '@ff/app-ui/copy';
import { Info, Loaded, Screen, Section } from '../ui/common.tsx';
import { QuickAskCard } from './asks.tsx';

type Home = z.infer<typeof H.Home>;
type Ledger = Home['whatWeDid'][number];

const LEDGER_ICON: Record<string, IconName> = {
  gbp_post: 'globe', gbp_photo: 'image', review_reply: 'reviews', site_change: 'edit', project_page: 'jobs', listing_fix: 'check',
  hours_change: 'clock', notice: 'flag', report: 'file', request_update: 'inbox', other: 'spark',
  // What the client's own team did (deck ruling c144). Staff reads never reach this feed.
  team_job: 'camera', team_review_ask: 'star', team_request: 'send', team_approval: 'check',
};

type TwoJobs = NonNullable<Home['twoJobs']>;

/**
 * "We had no jobs" (Round 2 ruling r01): the owner or a manager marks this week or this month, and every mark shows
 * with an Undo. The server's answer replaces the card, so the counts always match what's stored.
 */
function NoJobsMarks({ j }: { j: TwoJobs }): JSX.Element | null {
  const T = S.home.twoJobs;
  const act = useSend();
  const scope = useStore(location).scope;
  const marks = j.noJobs ?? { weeks: [], months: [] };
  const weekMarked = marks.weeks.includes(j.week);
  const monthMarked = marks.months.includes(j.month);
  const apply = (next: TwoJobs, said: string) => {
    setCached('home', { query: scope ? { location: scope } : {} }, (prev) => (prev ? { ...prev, twoJobs: next } : prev));
    invalidate('home');
    toast(said);
  };
  const mark = async (period: 'week' | 'month', n: number) => {
    const next = await act.run(() => send('noJobsMark', { body: { period, n } }));
    if (next) apply(next, T.marked);
  };
  const unmark = async (period: 'week' | 'month', n: number) => {
    const next = await act.run(() => send('noJobsUnmark', { params: { mark: `${period}-${n}` } }));
    if (next) apply(next, T.unmarked);
  };
  const all = [...marks.weeks.map((n) => ({ period: 'week' as const, n, label: T.markWeek(n) })), ...marks.months.map((n) => ({ period: 'month' as const, n, label: T.markMonth(n) }))];
  // Offer this week and last week (the Monday nudge is about last week), this month and last month, but only where
  // nothing happened and nothing's marked yet.
  const asked = new Set(j.weeksAsked ?? (j.thisWeekAsked ? [j.week] : []));
  const photos = new Set(j.monthsWithPhotos ?? (j.thisMonthPhotos ? [j.month] : []));
  const open = (period: 'week' | 'month', n: number) => n >= 1 && (period === 'week' ? n <= 12 && !asked.has(n) && !marks.weeks.includes(n) : !photos.has(n) && !marks.months.includes(n));
  const offers = [
    { period: 'week' as const, n: j.week, label: T.noJobsWeek },
    { period: 'week' as const, n: j.week - 1, label: T.noJobsLastWeek },
    { period: 'month' as const, n: j.month, label: T.noJobsMonth },
    { period: 'month' as const, n: j.month - 1, label: T.noJobsLastMonth },
  ].filter((o) => open(o.period, o.n));
  return (
    <div class="stack no-jobs" data-gap="s" aria-live="polite">
      {weekMarked && <p class="t-small"><Icon name="check" size={16} /> {T.markedWeek}</p>}
      {monthMarked && <p class="t-small"><Icon name="check" size={16} /> {T.markedMonth}</p>}
      {all.length > 0 && (
        <>
          <p class="t-small t-muted">{T.marks(marks.weeks.length, marks.months.length)}</p>
          <ul role="list" class="no-jobs-marks">
            {all.map((m) => (
              <li key={`${m.period}-${m.n}`}>
                <span class="t-small">{m.label}</span>{' '}
                <Button size="sm" variant="quiet" busy={act.busy} aria-label={T.undoLabel(m.label)} onClick={() => void unmark(m.period, m.n)}>
                  {T.undo}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      {offers.length > 0 && (
        <>
          <p class="t-small t-muted">{T.noJobsHint}</p>
          <div class="btn-row">
            {offers.map((o) => (
              <Button key={`${o.period}-${o.n}`} size="sm" busy={act.busy} onClick={() => void mark(o.period, o.n)}>
                {o.label}
              </Button>
            ))}
          </div>
        </>
      )}
      {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
    </div>
  );
}

/** "Your two jobs" as a weekly habit over the 90 days after go-live (deck rulings c012, c042). */
function TwoJobsCard({ j, scope, canMark }: { j: TwoJobs; scope: string | null; canMark: boolean }): JSX.Element {
  const T = S.home.twoJobs;
  return (
    <Section title={T.title} id="two-jobs" info={<Info title={T.info[0]} lines={[T.info[1], T.info[2]]} />} aside={<span class="t-small t-muted">{T.week(j.week)}</span>}>
      <Card>
        <p>{T.lead}</p>
        <ul role="list" class="two-jobs">
          <li>
            <ProgressRing done={j.reviewWeeks.done} total={j.reviewWeeks.total} size={64} label={<span><b>{j.reviewWeeks.done}</b></span>} srLabel={T.reviewWeeks(j.reviewWeeks.done)} />
            <div class="stack" data-gap="s">
              <p><strong>{T.reviewWeeks(j.reviewWeeks.done)}</strong></p>
              <p class="t-small t-muted">{T.reviewTarget}</p>
              <p class="t-small">
                <Icon name={j.thisWeekAsked ? 'check' : 'clock'} size={16} /> {j.thisWeekAsked ? T.askedThisWeek : T.notAskedThisWeek}
              </p>
              {(j.asksThisWeek !== undefined || j.tapsThisWeek !== undefined) && <p class="t-small t-muted">{T.thisWeek(j.asksThisWeek ?? 0, j.tapsThisWeek ?? 0)}</p>}
            </div>
          </li>
          <li>
            <ProgressRing done={j.photoMonths.done} total={j.photoMonths.total} size={64} label={<span><b>{j.photoMonths.done}</b></span>} srLabel={T.photoMonths(j.photoMonths.done)} />
            <div class="stack" data-gap="s">
              <p><strong>{T.photoMonths(j.photoMonths.done)}</strong></p>
              <p class="t-small t-muted">{T.photoTarget}</p>
              <p class="t-small">
                <Icon name={j.thisMonthPhotos ? 'check' : 'clock'} size={16} /> {j.thisMonthPhotos ? T.photosThisMonth : T.noPhotosThisMonth}
              </p>
            </div>
          </li>
        </ul>
        <p class="t-small t-muted">{T.ends(dayLabel(j.endsOn))}</p>
        {canMark ? <NoJobsMarks j={j} /> : j.noJobs && (j.noJobs.weeks.length > 0 || j.noJobs.months.length > 0) ? <p class="t-small t-muted">{T.marks(j.noJobs.weeks.length, j.noJobs.months.length)}</p> : null}
        <div class="card-foot btn-row">
          {!j.thisWeekAsked && (
            <Button variant="primary" href={scoped('/reviews/ask', scope)} icon="star">
              {T.ask}
            </Button>
          )}
          {!j.thisMonthPhotos && (
            <Button variant={j.thisWeekAsked ? 'primary' : 'secondary'} href={scoped('/jobs/new', scope)} icon="camera">
              {T.addPhotos}
            </Button>
          )}
        </div>
      </Card>
    </Section>
  );
}

/** The 7 day go-live clock (deck rulings c007, c008, c041): days used, days paused while we waited, the new date. */
function ClockCard({ clock }: { clock: NonNullable<NonNullable<Home['getLive']>['clock']> | null }): JSX.Element {
  const C = S.getLive.clock;
  if (!clock)
    return (
      <p class="getlive-date">
        <Icon name="clock" size={16} /> {C.notStarted}
      </p>
    );
  return (
    <div class="getlive-clock" role="group" aria-labelledby="clock-h">
      <div class="card-head">
        <h3 id="clock-h" class="sub-h">{C.title}</h3>
        <Info title={C.info[0]} lines={[C.info[1], C.info[2]]} />
      </div>
      <div class="bar" role="progressbar" aria-valuemin={0} aria-valuemax={clock.promiseDays} aria-valuenow={Math.min(clock.daysUsed, clock.promiseDays)} aria-label={C.used(clock.daysUsed)}>
        <span style={`inline-size:${Math.min(100, (clock.daysUsed / clock.promiseDays) * 100)}%`} />
      </div>
      <ul role="list" class="getlive-clock-facts">
        <li>{C.used(clock.daysUsed)}</li>
        <li>{C.paused(clock.daysPaused)}</li>
      </ul>
      {clock.pausedNow && (
        <p class="t-small">
          <Icon name="hourglass" size={16} /> {(clock.waitingOn && C.pausedFor[clock.waitingOn]) || C.pausedNow}
        </p>
      )}
      <p class="getlive-date">{C.deadline(dayLabel(clock.deadline))}</p>
    </div>
  );
}

export function LedgerRow({ item, zone }: { item: Ledger; zone: string }): JSX.Element {
  return (
    <li>
      <Row
        href={item.url ?? undefined}
        external={!!item.url}
        lead={<Icon name={LEDGER_ICON[item.kind] ?? 'spark'} size={22} />}
        title={item.title}
        meta={item.url ? `${shortDate(item.at, zone)} · ${S.home.seeIt}` : shortDate(item.at, zone)}
      />
    </li>
  );
}

export function HomeScreen(): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const q = useQuery('home', { query: loc.scope ? { location: loc.scope } : {} });
  const zone = useStore(tz);
  useEffect(() => {
    if (q.data?.tenant.timeZone) tz.set(q.data.tenant.timeZone);
  }, [q.data?.tenant.timeZone]);
  const name = q.data?.greetingName ?? firstName(s.me);
  const greeting = S.home.greeting(partOfDay(zone), name);
  return (
    <Screen title={S.home.title} titleHidden query={q}>
      <Loaded q={q} skeleton="cards">
        {(h) => (h.mode === 'onboarding' && h.getLive ? <GetLive h={h} greeting={greeting} /> : <LiveHome h={h} greeting={greeting} zone={zone} />)}
      </Loaded>
    </Screen>
  );
}

function BannerFor({ b }: { b: Home['banner'] }) {
  if (!b) return null;
  return (
    <Banner tone={b.kind === 'site_down' || b.kind === 'payment_failed' ? 'danger' : 'warn'} role="alert" title={b.text} action={b.link ? <Button size="sm" variant="secondary" href={b.link}>{S.billing.updateCard}</Button> : null} />
  );
}

function LiveHome({ h, greeting, zone }: { h: Home; greeting: string; zone: string }): JSX.Element {
  const s = useReady();
  const scope = useStore(location).scope;
  const m = s.membership;
  const since = h.since ? S.home.since(dayLabel(h.since.slice(0, 10)).split(',')[0]!) : null;
  return (
    <div class="stack" data-gap="l">
      <BannerFor b={h.banner} />
      <div class="greeting">
        <p class="t-title" aria-hidden="true">{greeting}</p>
        <p class="greeting-sub">
          {since && <span>{since}: </span>}
          {S.home.sinceSummary(h.newSince.leads, h.newSince.reviews, h.newSince.jobsPublished)}
        </p>
      </div>

      {h.needsYou.length > 0 && (
        <Section title={S.home.needsYou} id="needs-you">
          <div class="stack" data-gap="m">
            {h.needsYou.map((n) => (
              <Card key={n.link} class="needs-card">
                <p>{n.title}</p>
                <div class="card-foot">
                  <Button variant="primary" href={scoped(n.link, scope)} iconEnd="arrow-right">
                    {n.actionLabel}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {/* c133: the lowest effort way to ask sits near the top, one email and Send. */}
      {can(m, 'review_requests.create') && <QuickAskCard />}

      {h.newSince.items.length > 0 && (
        <Section title={S.home.newSince} id="new-since">
          <List>
            {h.newSince.items.map((i) => (
              <LedgerRow key={i.id} item={i} zone={zone} />
            ))}
          </List>
        </Section>
      )}

      {h.twoJobs && <TwoJobsCard j={h.twoJobs} scope={scope} canMark={can(m, 'two_jobs.mark')} />}

      {h.month && (
        <Section title={S.home.month} id="month" aside={<span class="t-small t-muted">{S.home.monthOf(monthLabel(h.month.month).split(' ')[0]!)}</span>}>
          <div class="stats">
            {h.month.metrics.map((mt) => {
              const info = S.home.metricInfo[mt.key]!;
              const trend = mt.lastMonth === null ? null : mt.value > mt.lastMonth ? 'up' : mt.value < mt.lastMonth ? 'down' : 'flat';
              return (
                <Stat
                  key={mt.key}
                  label={S.home.metric[mt.key]!}
                  value={mt.value}
                  trend={trend}
                  compare={mt.lastMonth === null ? S.home.noLastMonth : S.home.lastMonth(mt.lastMonth)}
                  stamp={mt.provenance.updatedAt ? S.app.updated(ago(mt.provenance.updatedAt, zone)) : null}
                  info={<Info title={info[0]} lines={[info[1], info[2]]} />}
                />
              );
            })}
          </div>
        </Section>
      )}

      {h.map && can(m, 'reports.read') && (
        <Section title={S.home.map} id="map" info={<Info title={S.home.mapInfo[0]} lines={S.home.mapInfo.slice(1)} />}>
          <Card>
            <div class="map-headline">
              <ProgressRing done={h.map.top3Points} total={h.map.totalPoints} size={96} label={<span><b>{h.map.top3Points}</b><small>/{h.map.totalPoints}</small></span>} srLabel={S.reports.mapOf(h.map.top3Points)} />
              <div class="stack" data-gap="s">
                <p>{S.home.mapLine(h.map.top3Points, h.map.totalPoints, h.map.keyword)}</p>
                {h.map.dayOnePoints !== null && <p class="t-muted">{S.home.mapDayOne(h.map.dayOnePoints)}</p>}
              </div>
            </div>
            <div class="card-foot">
              <Button href={scoped('/reports/map', scope)} variant="secondary" iconEnd="arrow-right">
                {S.home.mapSee}
              </Button>
            </div>
          </Card>
        </Section>
      )}

      {h.whatWeDid.length > 0 && (
        <Section title={S.home.whatWeDid} id="what-we-did" aside={<a href={scoped('/activity', scope)} class="section-link">{S.app.seeAll}</a>}>
          <List>
            {h.whatWeDid.map((i) => (
              <LedgerRow key={i.id} item={i} zone={zone} />
            ))}
          </List>
        </Section>
      )}

      <InstallCard />
      <PushCard compact />

      <Section title={S.home.quick} id="quick">
        <div class="quick-grid">
          {h.quickActions.map((a) => {
            const map: Record<string, [string, IconName]> = {
              ask_review: ['/reviews/ask', 'reviews'],
              add_job: ['/jobs/new', 'camera'],
              change_hours: ['/hours', 'clock'],
              new_request: ['/inbox/new', 'inbox'],
            };
            const [href, icon] = map[a]!;
            return (
              <a key={a} class="quick" href={scoped(href, scope)}>
                <Icon name={icon} />
                <span>{S.home.quickActions[a]}</span>
              </a>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function GetLive({ h, greeting }: { h: Home; greeting: string }): JSX.Element {
  const g = h.getLive!;
  const left = g.ring.total - g.ring.done;
  const q = useQuery('onboarding');
  return (
    <div class="stack" data-gap="l">
      <p class="t-title" aria-hidden="true">{greeting}</p>
      <Card tone="frame" class="getlive">
        <div class="getlive-top">
          <ProgressRing tone="frame" done={g.ring.done} total={g.ring.total} size={96} label={<span><b>{g.ring.done}</b><small>/{g.ring.total}</small></span>} srLabel={S.getLive.ring(g.ring.done, g.ring.total)} />
          <div class="stack" data-gap="s">
            <h2 class="t-heading">{S.getLive.title(left)}</h2>
            {g.next && <p class="t-muted">{S.getLive.nextUp(g.next.title.toLowerCase())} {S.getLive.stepTime[g.next.step]}.</p>}
          </div>
        </div>
        {g.next && (
          <Button variant="primary" block size="lg" href={g.next.link} iconEnd="arrow-right" class="on-frame">
            {g.next.actionLabel}
          </Button>
        )}
        {g.clock !== undefined ? (
          <ClockCard clock={g.clock} />
        ) : (
          g.goLiveBy && (
            <p class="getlive-date">
              {S.getLive.goLiveBy(dayLabel(g.goLiveBy))} {S.getLive.waited(g.waitingOnYouDays)}
            </p>
          )
        )}
      </Card>

      <Section title={S.getLive.waitingOnYou} id="waiting-on-you">
        {g.waitingOnYou.length ? (
          <List>
            {g.waitingOnYou.map((w) => (
              <li key={w.step}>
                <Row href={`/start/${w.step}`} lead={<Icon name="clock" size={22} />} title={w.label} meta={`${S.getLive.state[w.state]} · ${S.getLive.stepTime[w.step] ?? ''}`} />
              </li>
            ))}
          </List>
        ) : (
          <p class="t-muted">{S.inbox.nothingWaiting}</p>
        )}
      </Section>

      <Section title={S.getLive.weAreOnIt} id="we-are-on-it">
        <List>
          {g.weAreOnIt.map((w) => (
            <li key={w.key}>
              <Row lead={<Icon name={w.done ? 'check' : 'hourglass'} size={22} />} title={w.label} meta={w.done ? S.getLive.state.done : S.getLive.state.waiting_on_us} />
            </li>
          ))}
        </List>
      </Section>

      <div class="stack" data-gap="s">
        {g.previewUrl && (
          <Button href={g.previewUrl} external variant="secondary" icon="eye" block>
            {S.getLive.preview}
          </Button>
        )}
        {q.data?.bookingUrl && (
          <Button href={q.data.bookingUrl} external variant="secondary" icon="calendar" block>
            {S.getLive.stuck}
          </Button>
        )}
        <Button href="/inbox/new" variant="quiet" icon="help" block>
          {S.getLive.ask}
        </Button>
      </div>
      <InstallCard />
    </div>
  );
}

export function ActivityScreen(): JSX.Element {
  const loc = useStore(location);
  const q = useQuery('activity', { query: loc.scope ? { location: loc.scope } : {} });
  const zone = useStore(tz);
  return (
    <Screen title={S.home.activityTitle} back="/" lead={S.home.activityLead} query={q}>
      <Loaded q={q}>
        {(d) =>
          d.items.length ? (
            <List>
              {d.items.map((i) => (
                <LedgerRow key={i.id} item={i} zone={zone} />
              ))}
            </List>
          ) : (
            <EmptyState title={S.home.activityTitle} body={S.home.activityEmpty} />
          )
        }
      </Loaded>
    </Screen>
  );
}
